// Sections and dynamically loaded quick views can include this asset again.
// Keep declarations local and register the component only once.
(() => {
if (customElements.get("product-recommendations")) return;

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this.handleAddToCartClick);
    this.onBedPriceChange ||= () => {
      if (this.bedAddonProducts) this.renderProducts(this.bedAddonProducts);
    };
    document.addEventListener("bed:price-change", this.onBedPriceChange);

    if (this.dataset.loaded === "true" || this.loading) return;

    // Closed bundle drawers and later wizard steps do not need recommendations
    // during the initial product-page load. Start when they become visible.
    if ("IntersectionObserver" in window) {
      this.loadObserver?.disconnect();
      const observer = new IntersectionObserver((entries) => {
        if (this.loadObserver !== observer) return;
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.loadObserver = null;
        if (this.isConnected) this.load();
      }, { rootMargin: "200px 0px" });
      this.loadObserver = observer;
      observer.observe(this);
    } else {
      this.load();
    }
  }

  disconnectedCallback() {
    this.loadObserver?.disconnect();
    this.loadObserver = null;
    this.removeEventListener("click", this.handleAddToCartClick);
    document.removeEventListener("bed:price-change", this.onBedPriceChange);
  }

  handleAddToCartClick = (event) => {
    const button = event.target.closest("[data-product-recommendations-add]");

    if (!button || !this.contains(button) || button.disabled) {
      return;
    }

    event.preventDefault();
    this.addToCart(button);
  };

  get productTemplate() {
    return this.querySelector(
      "[data-product-recommendations-product-template]"
    );
  }

  get listElement() {
    return this.querySelector("[data-product-recommendations-list]");
  }

  get statusElement() {
    return this.querySelector("[data-product-recommendations-status]");
  }

  getIntent() {
    const allowedIntents = ["complementary", "related"];
    return allowedIntents.includes(this.dataset.intent)
      ? this.dataset.intent
      : "related";
  }

  getProductId() {
    return (
      this.dataset.productId ||
      this.closest("[data-product-id]")?.dataset?.productId ||
      null
    );
  }

  usesBedAddons() {
    return this.hasAttribute("data-use-bed-addons");
  }

  getBedAddonHandles() {
    const handles =
      this.dataset.bedAddonHandles ||
      this.closest("[data-bed-addon-handles]")?.dataset?.bedAddonHandles;

    if (!handles) {
      return [];
    }

    return handles
      .split(",")
      .map((handle) => handle.trim())
      .filter(Boolean);
  }

  setStatusVisible(isVisible) {
    if (this.statusElement) {
      this.statusElement.hidden = !isVisible;
    }
  }

  setListVisible(isVisible) {
    if (this.listElement) {
      this.listElement.hidden = !isVisible;
    }
  }

  getProductImageUrl(product) {
    if (!product.featured_image) {
      return null;
    }

    if (typeof product.featured_image === "string") {
      return product.featured_image.startsWith("//")
        ? `https:${product.featured_image}`
        : product.featured_image;
    }

    return null;
  }

  getFirstAvailableVariant(product) {
    const variants = product?.variants || [];

    return (
      variants.find((variant) => variant.available) ||
      variants[0] ||
      null
    );
  }

  getInsurancePremium(bedPrice) {
    if (!Number.isFinite(bedPrice) || bedPrice < 20000) return null;
    if (bedPrice < 50000) return 4300;
    if (bedPrice < 100000) return 7600;
    if (bedPrice < 150000) return 10900;
    if (bedPrice < 200000) return 14500;
    if (bedPrice < 300000) return 19700;
    return null;
  }

  getCardVariant(product) {
    if (!this.usesBedAddons() || product.handle !== "insurance-product") {
      return this.getFirstAvailableVariant(product);
    }

    const bedPrice = Number(this.closest("addons-drawer")?.dataset.bedPriceCents);
    const premium = this.getInsurancePremium(bedPrice);
    return premium == null ? null : product.variants?.find(
      (variant) => variant.available && Number(variant.price) === premium
    ) || null;
  }

  populateProductCard(fragment, product, moneyFormat) {
    const imageWrap = fragment.querySelector(
      "[data-product-recommendations-image]"
    );
    const imageElement = fragment.querySelector(
      "[data-product-recommendations-image-element]"
    );
    const titleElement = fragment.querySelector(
      "[data-product-recommendations-title]"
    );
    const priceElement = fragment.querySelector(
      "[data-product-recommendations-price]"
    );
    const addButton = fragment.querySelector(
      "[data-product-recommendations-add]"
    );
    const imageUrl = this.getProductImageUrl(product);
    const variant = this.getCardVariant(product);
    const isInsurance = this.usesBedAddons() && product.handle === "insurance-product";

    if (titleElement) {
      titleElement.textContent = product.title || "";
    }

    if (priceElement) {
      priceElement.textContent = variant
        ? FoxTheme.Currency.formatMoney(variant.price, moneyFormat)
        : isInsurance ? "Cover price unavailable for this bed" : "Unavailable";
    }

    if (addButton) {
      if (variant?.id) addButton.setAttribute("data-variant-id", String(variant.id));
      else addButton.removeAttribute("data-variant-id");
      addButton.removeAttribute("data-added");

      if (variant?.available) {
        addButton.removeAttribute("disabled");
        addButton.removeAttribute("aria-disabled");
      } else {
        addButton.setAttribute("disabled", "");
        addButton.setAttribute("aria-disabled", "true");
      }
    }

    if (isInsurance) {
      const details = fragment.querySelector(".addon-card__details");
      if (details) {
        const terms = document.createElement("a");
        terms.href = "/pages/warranty";
        terms.className = "addon-view-info";
        terms.textContent = variant ? "Cover details & terms" : "See cover details and available tiers";
        details.appendChild(terms);
      }
    }

    if (imageUrl && imageWrap && imageElement) {
      imageElement.src = imageUrl;
      imageElement.alt = product.title || "";
      imageWrap.hidden = false;
    } else if (imageWrap) {
      imageWrap.hidden = true;
    }
  }

  shouldSuppressCartDrawer() {
    return Boolean(this.closest("addons-drawer"));
  }

  getSectionsToRender() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", {
        bubbles: true,
        detail: { sections },
      })
    );
    return sections;
  }

  getVariantIdFromButton(button) {
    return button.getAttribute("data-variant-id") || button.dataset.variantId || "";
  }

  isCartAddError(parsedState) {
    return Boolean(
      parsedState?.status && !parsedState?.items?.length && parsedState?.description
    );
  }

  setAddButtonLoading(button, isLoading) {
    button.classList.toggle("btn--loading", isLoading);

    if (isLoading) {
      button.setAttribute("disabled", "");
      button.setAttribute("aria-disabled", "true");
      return;
    }

    if (!button.hasAttribute("data-added")) {
      button.removeAttribute("disabled");
      button.removeAttribute("aria-disabled");
    }
  }

  async refreshCartState(parsedState) {
    const cartJson = await (
      await fetch(`${FoxTheme.routes.cart_url}`, {
        ...FoxTheme.utils.fetchConfig(),
      })
    ).json();

    cartJson.sections = parsedState.sections;

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: cartJson,
    });

    document.dispatchEvent(
      new CustomEvent("product-ajax:added", {
        detail: {
          product: parsedState,
        },
      })
    );
  }

  async addToCart(button) {
    const variantId = this.getVariantIdFromButton(button);

    if (!variantId || button.hasAttribute("data-added")) {
      return;
    }

    this.setAddButtonLoading(button, true);

    try {
      const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
        ...FoxTheme.utils.fetchConfig("javascript"),
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: this.getSectionsToRender(),
          section_url: window.location.pathname,
        }),
      });

      const parsedState = await response.json();

      if (this.isCartAddError(parsedState)) {
        document.dispatchEvent(
          new CustomEvent("product-ajax:error", {
            detail: {
              errorMessage: parsedState.description,
            },
          })
        );
        return;
      }

      button.setAttribute("data-added", "");

      try {
        await this.refreshCartState(parsedState);
      } catch (error) {
        console.error(error);
      }

      if (!this.shouldSuppressCartDrawer()) {
        document.querySelector("cart-drawer")?.show?.(button);
      }
    } catch (error) {
      console.error(error);
      document.dispatchEvent(
        new CustomEvent("product-ajax:error", {
          detail: {
            errorMessage: error.message,
          },
        })
      );
    } finally {
      this.setAddButtonLoading(button, false);
    }
  }

  renderProducts(products) {
    const template = this.productTemplate;
    const list = this.listElement;

    if (!template || !list) {
      this.handleEmpty();
      return;
    }

    const moneyFormat = FoxTheme.settings.moneyFormat;

    list.replaceChildren();

    products.forEach((product) => {
      const fragment = template.content.cloneNode(true);
      this.populateProductCard(fragment, product, moneyFormat);
      list.append(...fragment.children);
    });

    this.setStatusVisible(false);
    this.setListVisible(true);
  }

  renderFromSectionContent(content) {
    const sectionList =
      content.querySelector("[data-product-recommendations-list]") ||
      content.querySelector(".product-recommendations__list");
    const list = this.listElement;

    if (!sectionList || !list || !sectionList.children.length) {
      return false;
    }

    list.replaceChildren(
      ...[...sectionList.children].map((child) => child.cloneNode(true))
    );
    this.setStatusVisible(false);
    this.setListVisible(true);
    return true;
  }

  usesSectionMarkupUrl() {
    return Boolean(this.dataset.url);
  }

  getSectionMarkupSelector() {
    if (this.classList.contains("complementary-products")) {
      return "product-recommendations.complementary-products";
    }

    if (this.classList.contains("related-products")) {
      return "product-recommendations.related-products";
    }

    return "product-recommendations";
  }

  findSectionMarkupSource(document) {
    const selector = this.getSectionMarkupSelector();

    return (
      document.querySelector(selector) ||
      document.querySelector("product-recommendations")
    );
  }

  sectionMarkupHasProducts(source) {
    return Boolean(
      source.querySelector(
        ".complementary-products__container, .product-card, .products-grid"
      )
    );
  }

  hideSectionMarkupBlock() {
    this.closest(".product__block--complementary")?.classList.add("hidden");
    this.closest(".section--related-products")?.classList.add("hidden");
  }

  loadFromSectionMarkupUrl() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const source = this.findSectionMarkupSource(html);

        if (!source || !this.sectionMarkupHasProducts(source)) {
          this.handleSectionMarkupEmpty();
          return;
        }

        this.innerHTML = source.innerHTML;
        this.markLoaded("recommendations:loaded");
      })
      .catch(() => {
        this.handleSectionMarkupEmpty();
      });
  }

  handleSectionMarkupEmpty() {
    this.replaceChildren();
    this.hideSectionMarkupBlock();
    this.markLoaded("recommendations:empty");
  }

  load() {
    if (this.loading || this.dataset.loaded === "true") return;
    this.loading = true;
    if (this.usesSectionMarkupUrl()) {
      this.loadFromSectionMarkupUrl();
      return;
    }

    const productId = this.getProductId();
    const limit = parseInt(this.dataset.limit || "6", 10);
    const intent = this.getIntent();
    const sectionId = document.querySelector(
      'meta[name="product-recommendations-section-id"]'
    )?.content;
    const recommendationsUrl =
      FoxTheme.routes?.product_recommendations_url ||
      "/recommendations/products";

    if (!productId) {
      this.handleEmpty();
      return;
    }

    if (this.usesBedAddons()) {
      this.loadBedAddons(limit).then((loaded) => {
        if (!loaded) {
          this.loadRecommendations(
            productId,
            limit,
            intent,
            sectionId,
            recommendationsUrl
          );
        }
      });
      return;
    }

    this.loadRecommendations(
      productId,
      limit,
      intent,
      sectionId,
      recommendationsUrl
    );
  }

  loadBedAddons(limit) {
    const handles = this.getBedAddonHandles().slice(0, limit);

    if (!handles.length) {
      return Promise.resolve(false);
    }

    const root = window.Shopify?.routes?.root || "/";

    return Promise.all(
      handles.map((handle) =>
        fetch(`${root}products/${handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    )
      .then((products) => {
        const validProducts = products.filter((product) => product?.id);

        if (!validProducts.length) {
          return false;
        }

        this.bedAddonProducts = validProducts;
        this.renderProducts(validProducts);
        this.markLoaded("recommendations:loaded");
        return true;
      })
      .catch(() => false);
  }

  loadRecommendations(productId, limit, intent, sectionId, recommendationsUrl) {
    if (sectionId) {
      const sectionMarkupUrl = `${recommendationsUrl}?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=${intent}`;

      fetch(sectionMarkupUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const content = new DOMParser()
            .parseFromString(responseText, "text/html")
            .querySelector("[data-product-recommendations-content]");

          if (
            content?.innerHTML.trim() &&
            this.renderFromSectionContent(content)
          ) {
            this.markLoaded("recommendations:loaded");
            return;
          }

          this.loadFromJson(productId, limit, intent);
        })
        .catch(() => {
          this.loadFromJson(productId, limit, intent);
        });

      return;
    }

    this.loadFromJson(productId, limit, intent);
  }

  loadFromJson(productId, limit, intent) {
    const resolvedIntent = intent || this.getIntent();
    const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${resolvedIntent}`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const products = data?.products || [];

        if (!products.length) {
          this.handleEmpty();
          return;
        }

        this.renderProducts(products);
        this.markLoaded("recommendations:loaded");
      })
      .catch(() => {
        this.handleEmpty();
      });
  }

  markLoaded(eventName) {
    this.loading = false;
    this.dataset.loaded = "true";
    this.dispatchEvent(new CustomEvent(eventName));
  }

  handleEmpty() {
    if (this.listElement) {
      this.listElement.replaceChildren();
      this.listElement.hidden = true;
    }

    this.setStatusVisible(false);
    this.markLoaded("recommendations:empty");
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}

})();
